import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api, { formatApiError } from "../api";
import { BrutalButton, BrutalCard, BrutalInput } from "../components/Brutal";
import { Package, Eye, EyeSlash, WarningCircle } from "@phosphor-icons/react";

export default function ResetPasswordPage() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [valid, setValid] = useState(null); // null = checking
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) { setValid(false); return; }
    api.get(`/auth/reset-password/validate?token=${encodeURIComponent(token)}`)
      .then(({ data }) => setValid(!!data.valid))
      .catch(() => setValid(false));
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error(t("auth.reset_too_short")); return; }
    if (password !== confirm) { toast.error(t("auth.passwords_no_match")); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      nav("/login?reset=1");
    } catch (err) {
      toast.error(formatApiError(err));
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-200px)] flex items-center justify-center py-8">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="w-14 h-14 bg-white border border-[#c9a24a] rounded-xl shadow-sm flex items-center justify-center">
              <Package size={28} weight="bold" color="#c9a24a" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">ReUP</h1>
          </div>
        </div>

        <BrutalCard data-testid="reset-password-card">
          {valid === null && (
            <p className="text-sm text-zinc-500 text-center py-6" data-testid="reset-checking">
              {t("auth.reset_checking")}
            </p>
          )}
          {valid === false && (
            <div className="text-center py-4" data-testid="reset-invalid">
              <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-rose-50 border border-rose-300 flex items-center justify-center">
                <WarningCircle size={24} weight="bold" className="text-rose-600" />
              </div>
              <h2 className="text-xl font-semibold mb-2 tracking-tight">{t("auth.reset_invalid_title")}</h2>
              <p className="text-sm text-zinc-600 leading-relaxed">{t("auth.reset_invalid_body")}</p>
              <Link to="/forgot-password"
                className="inline-block mt-4 text-sm font-semibold text-[#8a6a1c] hover:underline"
                data-testid="request-new-link">
                {t("auth.request_new_link")}
              </Link>
            </div>
          )}
          {valid === true && (
            <>
              <h2 className="text-2xl font-semibold mb-2 tracking-tight">{t("auth.reset_title")}</h2>
              <p className="text-sm text-zinc-600 mb-6">{t("auth.reset_hint")}</p>
              <form onSubmit={onSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">{t("auth.new_password")}</label>
                  <div className="relative">
                    <BrutalInput
                      data-testid="reset-password-input"
                      type={show ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={6}
                      className="pr-12"
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => setShow(!show)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-[#c9a24a] transition-colors p-1"
                      data-testid="reset-toggle-visibility"
                    >
                      {show ? <EyeSlash size={22} /> : <Eye size={22} />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">{t("auth.confirm_password")}</label>
                  <BrutalInput
                    data-testid="reset-confirm-input"
                    type={show ? "text" : "password"}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                    minLength={6}
                  />
                  {confirm && password !== confirm && (
                    <p className="text-xs text-rose-600 mt-1" data-testid="reset-mismatch-hint">
                      {t("auth.passwords_no_match")}
                    </p>
                  )}
                </div>
                <BrutalButton
                  type="submit"
                  size="lg"
                  disabled={loading}
                  className="w-full"
                  data-testid="reset-submit-button"
                >
                  {loading ? t("auth.reset_saving") : t("auth.reset_submit")}
                </BrutalButton>
              </form>
            </>
          )}
          <p className="text-sm text-zinc-600 mt-6 text-center">
            <Link to="/login" className="font-medium text-[#8a6a1c] hover:underline" data-testid="reset-back-to-login">
              {t("auth.back_to_login")}
            </Link>
          </p>
        </BrutalCard>
      </div>
    </div>
  );
}
